/**
 * Color utility functions for calculating contrast and working with colors.
 */

/**
 * Calculates the relative luminance of a hex color.
 * Uses the WCAG formula for calculating luminance.
 *
 * @param hexColor - Hex color string (e.g., '#FF5733' or 'FF5733')
 * @returns Luminance value between 0 (black) and 1 (white)
 */
export function getLuminance(hexColor: string): number {
    const hex = hexColor.replace('#', '');

    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const toLinear = (c: number): number => {
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Determines whether to use light or dark text on a given background color.
 * Returns white for dark backgrounds and a dark color for light backgrounds.
 *
 * @param backgroundColor - Hex color string of the background
 * @param lightColor - Color to use on dark backgrounds (default: '#ffffff')
 * @param darkColor - Color to use on light backgrounds (default: '#1a1a1a')
 * @returns The contrasting text color
 */
export function getContrastingTextColor(
    backgroundColor: string,
    lightColor: string = '#ffffff',
    darkColor: string = '#1a1a1a'
): string {
    const luminance = getLuminance(backgroundColor);
    // Use white text if luminance is below 0.5 (darker backgrounds)
    return luminance < 0.5 ? lightColor : darkColor;
}

/**
 * Validates if a string is a valid hex color.
 *
 * @param color - String to validate
 * @returns True if the string is a valid hex color
 */
export function isValidHexColor(color: string | null | undefined): boolean {
    if (!color) {
        return false;
    }
    return /^#?([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(color);
}

/**
 * Normalizes a hex color to always include the # prefix.
 *
 * @param color - Hex color string
 * @returns Normalized hex color with # prefix
 */
export function normalizeHexColor(color: string): string {
    if (!color) {
        return color;
    }
    return color.startsWith('#') ? color : `#${color}`;
}

/**
 * Utility class for avatar-related color operations.
 */
export class ColorUtils {

    /**
     * Predefined color palette with good contrast for initials display.
     * Colors are designed to work well with either white or dark text.
     */
    private static readonly COLOR_PALETTE: string[] = [
        '#E53935', // Red
        '#D81B60', // Pink
        '#8E24AA', // Purple
        '#5E35B1', // Deep Purple
        '#3949AB', // Indigo
        '#1E88E5', // Blue
        '#039BE5', // Light Blue
        '#00ACC1', // Cyan
        '#00897B', // Teal
        '#43A047', // Green
        '#7CB342', // Light Green
        '#C0CA33', // Lime
        '#FDD835', // Yellow
        '#FFB300', // Amber
        '#FB8C00', // Orange
        '#F4511E', // Deep Orange
        '#6D4C41', // Brown
        '#757575', // Grey
        '#546E7A', // Blue Grey
    ];

    /**
     * Generates a consistent hash code from a string.
     * Same string will always produce the same hash.
     */
    private static hashCode(str: string): number {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash &= hash; // Convert to 32bit integer
        }
        return Math.abs(hash);
    }

    /**
     * Gets a consistent background color for a given string.
     * The same string will always return the same color.
     *
     * @param str - The string to generate a color for (e.g., show name)
     * @returns A hex color string
     */
    public static getColorForString(str: string): string {
        if (!str || str.trim().length === 0) {
            return this.COLOR_PALETTE[0];
        }
        const hash = this.hashCode(str.toLowerCase().trim());
        const index = hash % this.COLOR_PALETTE.length;
        return this.COLOR_PALETTE[index];
    }

    /**
     * Gets the appropriate text color (white or dark) for a given background color.
     * Uses WCAG luminance calculation for accurate contrast.
     *
     * @param backgroundColor - The background color hex string
     * @returns '#ffffff' or '#1a1a1a' depending on background brightness
     */
    public static getTextColorForBackground(backgroundColor: string): string {
        return getContrastingTextColor(backgroundColor);
    }

    /**
     * Extracts initials from a name string.
     * - Single word: First two characters (e.g., "CARO" -> "CA")
     * - Multiple words: First letter of first two words (e.g., "My Show" -> "MS")
     *
     * @param name - The name to extract initials from
     * @returns The initials (1-2 characters), uppercased
     */
    public static getInitials(name: string): string {
        if (!name || name.trim().length === 0) {
            return '?';
        }

        const words = name.trim().split(/\s+/);

        if (words.length === 1) {
            // Single word: take first two characters
            return words[0].substring(0, 2).toUpperCase();
        }

        // Multiple words: take first letter of first two words
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }

    /**
     * Gets all styling info for an avatar/initials display.
     *
     * @param name - The name to generate avatar info for
     * @returns Object with initials, backgroundColor, and textColor
     */
    public static getAvatarStyle(name: string): { initials: string; backgroundColor: string; textColor: string } {
        const backgroundColor = this.getColorForString(name);
        return {
            initials: this.getInitials(name),
            backgroundColor,
            textColor: this.getTextColorForBackground(backgroundColor)
        };
    }
}
